import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { Card, Button, Input, Select, Field, Stat, Textarea } from "@/components/UI";
import { Plus, PencilSimple, Trash, Plant, CurrencyCircleDollar, Basket, Handshake, ChartBar, Ruler } from "@phosphor-icons/react";
import { toast } from "sonner";
import { EMPTY_CROP, HARVEST_UNIT_OPTIONS, today, fmt } from "./constants";


export function OverviewTab({ crops, reload }) {
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState(EMPTY_CROP);
  const [editId, setEditId] = useState(null);

  const submit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name: form.name,
        notes: form.notes,
        area_dekar: form.area_dekar === "" ? 0 : Number(form.area_dekar),
      };
      if (editId) {
        await api.patch(`/fields/crops/${editId}`, payload);
        toast.success("Ürün güncellendi");
      } else {
        await api.post("/fields/crops", payload);
        toast.success("Ürün eklendi");
      }
      setForm(EMPTY_CROP); setEditId(null); setShowForm(false);
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Hata");
    }
  };

  const startEdit = (c) => {
    setEditId(c.id);
    setForm({ name: c.name, area_dekar: c.area_dekar ?? "", notes: c.notes || "" });
    setShowForm(true);
  };

  const del = async (c) => {
    if (!window.confirm(`"${c.name}" ürünü silinsin mi? Bağlı gider ve hasat kayıtları da silinir.`)) return;
    try {
      await api.delete(`/fields/crops/${c.id}`);
      toast.success("Silindi");
      reload();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Hata");
    }
  };

  return (
    <Card>
      <div className="flex items-center justify-between mb-3">
        <h3 className="heading text-lg">Ürünler</h3>
        <Button data-testid="fields-add-crop-btn" onClick={() => { setEditId(null); setForm(EMPTY_CROP); setShowForm(v => !v); }}>
          <Plus size={14} className="inline mr-1" /> {showForm ? "İptal" : "Yeni Ürün"}
        </Button>
      </div>
      {showForm && (
        <form onSubmit={submit} className="space-y-3 mb-4 p-4 bg-[#F5F2EC] rounded-md">
          <Field label="Ürün Adı *">
            <Input required data-testid="fields-crop-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} placeholder="Örn: Mısır Silajı, Yonca" />
          </Field>
          <Field label="Büyüklük (Dekar)">
            <Input type="number" step="0.1" min="0" data-testid="fields-crop-area"
                   value={form.area_dekar}
                   onChange={e => setForm({ ...form, area_dekar: e.target.value })}
                   placeholder="Örn: 40 (opsiyonel — dekar bazlı analiz için)" />
          </Field>
          <Field label="Notlar"><Textarea rows={2} value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} placeholder="Opsiyonel" /></Field>
          <div className="flex gap-2">
            <Button type="submit" data-testid="fields-crop-submit">{editId ? "Güncelle" : "Ekle"}</Button>
            <Button type="button" onClick={() => { setShowForm(false); setEditId(null); setForm(EMPTY_CROP); }} className="!bg-white !text-[#1A1D1A] border border-[#E5E2DC]">Vazgeç</Button>
          </div>
          <div className="text-xs text-[#5e675e]">
            <b>Dekar</b> girerseniz sistem otomatik olarak "dekar başına maliyet", "dekar başına verim" ve "birim üretim maliyeti" analizini hesaplar.
            Birim ve piyasa fiyatını Hasat kaydı sırasında gireceksiniz.
          </div>
        </form>
      )}

      {!crops.length ? (
        <div className="text-center py-10 text-[#8a938c]">
          <Plant size={40} className="mx-auto mb-2 opacity-50" />
          Henüz ürün yok. "Yeni Ürün" ile mısır silajı, buğday silajı, yonca gibi ürünler ekleyin.
        </div>
      ) : (
        <table className="data-table">
          <thead>
            <tr>
              <th>Ürün</th>
              <th className="text-right">Büyüklük</th>
              <th>Notlar</th>
              <th></th>
            </tr>
          </thead>
          <tbody>
            {crops.map(c => (
              <tr key={c.id} data-testid={`fields-crop-row-${c.id}`}>
                <td className="font-semibold">{c.name}</td>
                <td className="text-right tabnum">
                  {c.area_dekar ? <span className="text-[#2D4A36]"><b>{fmt(c.area_dekar)}</b> <span className="text-xs text-[#8a938c]">dekar</span></span> : <span className="text-xs text-[#8a938c]">—</span>}
                </td>
                <td className="text-xs text-[#6b746c]">{c.notes || "—"}</td>
                <td className="text-right whitespace-nowrap">
                  <button data-testid={`fields-crop-edit-${c.id}`} onClick={() => startEdit(c)} className="p-1.5 hover:bg-[#f2ede4] rounded"><PencilSimple size={16} /></button>
                  <button data-testid={`fields-crop-del-${c.id}`} onClick={() => del(c)} className="p-1.5 hover:bg-[#fbeee9] text-[#C45B45] rounded ml-1"><Trash size={16} /></button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </Card>
  );
}

// ---------------- EXPENSES TAB ----------------
